import { useEffect, useState } from 'react';

/** Great-circle distance in metres. */
export function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6_371_000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * The user's position, or null.
 *
 * Deliberately passive: it asks once and never nags. A traveller who declines
 * still gets a fully usable app — distance sorting and the "within 2 km" filter
 * simply do not appear.
 */
export function useGeolocation() {
    const [pos, setPos] = useState<{ lat: number; lng: number } | null>(null);

    useEffect(() => {
        if (!('geolocation' in navigator)) return;
        let cancelled = false;
        const id = navigator.geolocation.watchPosition(
            (p) => { if (!cancelled) setPos({ lat: p.coords.latitude, lng: p.coords.longitude }); },
            () => { /* denied or unavailable — the app works without it */ },
            { enableHighAccuracy: false, maximumAge: 60_000, timeout: 12_000 },
        );
        return () => { cancelled = true; navigator.geolocation.clearWatch(id); };
    }, []);

    return pos;
}

/** Bearing from a point to the Kaaba, in degrees clockwise from true north. */
export function qiblaBearing(lat: number, lng: number): number {
    const KAABA = { lat: 21.4224779, lng: 39.8251832 };
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLng = toRad(KAABA.lng - lng);
    const y = Math.sin(dLng) * Math.cos(toRad(KAABA.lat));
    const x = Math.cos(toRad(lat)) * Math.sin(toRad(KAABA.lat))
        - Math.sin(toRad(lat)) * Math.cos(toRad(KAABA.lat)) * Math.cos(dLng);
    return (((Math.atan2(y, x) * 180) / Math.PI) + 360) % 360;
}
